import React, { useEffect, useRef, useState } from 'react'
import { NavLink } from 'react-router-dom'
import axios from 'axios'
import { useAuth } from '@/contexts/AuthContext'
import { useFloosball } from '@/contexts/FloosballContext'
import { useSeasonWebSocket } from '@/contexts/SeasonWebSocketContext'
import { UserDropdown } from '@/Components/Navbar'
import { FavoriteTeamModal } from '@/Components/Auth/FavoriteTeamModal'
import ShopModal from '@/Components/Shop/ShopModal'
import { FloobitSymbol } from '@/Components/Icons/Floobit'
import CommandPalette from './CommandPalette'
import HoverTooltip from '@/Components/HoverTooltip'
import { useFantasySnapshot } from '@/hooks/useFantasySnapshot'
import { useIsMobile } from '@/hooks/useIsMobile'
import { useActiveEndowment } from '@/hooks/useActiveEndowment'
import { BG, BORDER, TEXT, ACCENT, FONT, TABULAR, SHELL_MOBILE_MAX, font } from './tokens'

const API_BASE = process.env.REACT_APP_API_URL || 'http://localhost:8000/api'

const REGULAR_SEASON_WEEKS = 28

const TrophyIcon: React.FC = () => (
  <svg width="15" height="15" viewBox="0 0 20 20" fill={ACCENT.warning} style={{ flexShrink: 0 }}>
    <path d="M5 2h10v1h3v3a3 3 0 01-3 3h-.4A5 5 0 0111 11.9V14h2.5a1 1 0 011 1v1h-9v-1a1 1 0 011-1H9v-2.1A5 5 0 015.4 9H5a3 3 0 01-3-3V3h3V2zm0 2H3.5v2A1.5 1.5 0 005 7.5V4zm10 0v3.5A1.5 1.5 0 0016.5 6V4H15z" />
  </svg>
)

const SearchIcon: React.FC = () => (
  // currentColor, so the button it sits in can light it up on hover.
  <svg width="14" height="14" viewBox="0 0 20 20" fill="currentColor" style={{ flexShrink: 0 }}>
    <path d="M8 3a5 5 0 013.9 8.1l4 4-1.4 1.4-4-4A5 5 0 118 3zm0 2a3 3 0 100 6 3 3 0 000-6z" />
  </svg>
)

/**
 * The top header: identity on the left, the user on the right.
 *
 * The reigning-champion block replaced a "N games live" pill in review — the live count
 * already lives on the Games nav entry and in the front page's own band, so the header
 * slot was better spent on something no other surface shows.
 *
 * The Criticality and Rulebook indicators that used to sit here are deliberately gone:
 * anomaly status moves to the front page and the active ruleset to the game board, where
 * each has room to say something rather than being a lone glyph in the chrome.
 */
const MenuIcon: React.FC = () => (
  <svg width="20" height="20" viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="2">
    <path strokeLinecap="round" d="M3 5h14M3 10h14M3 15h14" />
  </svg>
)

/** `onOpenNav` is passed only on phones, where the nav is a drawer. */
const AppHeader: React.FC<{ onOpenNav?: () => void }> = ({ onOpenNav }) => {
  const isMobile = useIsMobile(SHELL_MOBILE_MAX)
  const { seasonState } = useFloosball()
  const { user, getToken } = useAuth()
  const { event: wsEvent } = useSeasonWebSocket()
  const endowment = useActiveEndowment()

  const [champion, setChampion] = useState<any>(null)
  const [showUserMenu, setShowUserMenu] = useState(false)
  const [showTeamPicker, setShowTeamPicker] = useState(false)
  const [showShop, setShowShop] = useState(false)
  const [notifications, setNotifications] = useState<any[]>([])
  const [unreadCount, setUnreadCount] = useState(0)
  const [showPalette, setShowPalette] = useState(false)

  const getTokenRef = useRef(getToken)
  getTokenRef.current = getToken

  // The reigning champion only changes at season end, but a stale cached value here is
  // very visible, so the cache is burned on every call.
  useEffect(() => {
    let cancelled = false
    const load = async () => {
      try {
        const res = await axios.get(`${API_BASE}/reigning-champion?t=${Date.now()}`)
        if (!cancelled) setChampion(res.data?.data || null)
      } catch { if (!cancelled) setChampion(null) }
    }
    load()
    return () => { cancelled = true }
  }, [])

  useEffect(() => {
    // ⚠️ `.event`, NOT `.type`. Every season-socket payload is keyed `event` (see
    // api/event_models.py); `.type` is only ever sent BY the client, on identify and
    // watch. Reading `.type` here yielded undefined for every message, so this effect
    // never fired once and the panel below only ever refreshed on mount.
    const type = (wsEvent as any)?.event ?? (wsEvent as any)?.type
    if (type === 'season_end' || type === 'season_start') {
      axios.get(`${API_BASE}/reigning-champion?t=${Date.now()}`)
        .then(res => setChampion(res.data?.data || null))
        .catch(() => { /* keep the last known champion */ })
    }
  }, [wsEvent])

  useEffect(() => {
    if (!user) { setUnreadCount(0); return }
    let cancelled = false
    const load = async () => {
      try {
        const tok = await getTokenRef.current()
        if (!tok) return
        const res = await fetch(`${API_BASE}/notifications/count`, { headers: { Authorization: `Bearer ${tok}` } })
        if (!res.ok) return
        const data = await res.json()
        if (!cancelled) setUnreadCount(data.unread || 0)
      } catch { /* keep last */ }
    }
    load()
    const id = setInterval(load, 30_000)
    return () => { cancelled = true; clearInterval(id) }
  }, [user])

  useEffect(() => {
    if (!showUserMenu || !user) return
    const load = async () => {
      try {
        const tok = await getTokenRef.current()
        if (!tok) return
        const res = await fetch(`${API_BASE}/notifications`, { headers: { Authorization: `Bearer ${tok}` } })
        if (!res.ok) return
        const data = await res.json()
        setNotifications(data.notifications || [])
      } catch { /* keep last */ }
    }
    load()
  }, [showUserMenu, user])

  const markAllRead = async () => {
    try {
      const tok = await getTokenRef.current()
      if (!tok) return
      await fetch(`${API_BASE}/notifications/read`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${tok}` },
        body: JSON.stringify({ all: true }),
      })
      setNotifications(prev => prev.map(n => ({ ...n, isRead: true })))
      setUnreadCount(0)
    } catch { /* keep last */ }
  }

  // ⚠️ The picker is NO LONGER forced open (owner). Landing on a modal before you
  // have seen anything is the worst moment to ask someone to pick a club — the
  // front page's own team panel offers it instead, once there is a league on
  // screen to pick from. The modal itself stays: `floosball:open-team-picker`
  // opens it from the team panel, the achievements page and anywhere else.
  useEffect(() => {
    if (!user) setShowUserMenu(false)
  }, [user])

  useEffect(() => {
    const openTeamPicker = () => setShowTeamPicker(true)
    const openShop = () => setShowShop(true)
    window.addEventListener('floosball:show-favorite-team-picker', openTeamPicker)
    window.addEventListener('floosball:show-shop', openShop)
    return () => {
      window.removeEventListener('floosball:show-favorite-team-picker', openTeamPicker)
      window.removeEventListener('floosball:show-shop', openShop)
    }
  }, [])

  const weekLabel = seasonState.seasonComplete
    ? seasonState.currentWeekText
    : seasonState.currentWeek > REGULAR_SEASON_WEEKS
      ? seasonState.currentWeekText
      : `Week ${seasonState.currentWeek} of ${REGULAR_SEASON_WEEKS}`

  return (
    <>
      <header
        className="font-pixel"
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: isMobile ? '10px' : '22px',
          padding: isMobile ? '10px 12px' : '14px 22px',
          background: BG.panel,
          borderBottom: `1px solid ${BORDER.hairline}`,
          fontFamily: FONT,
        }}
      >
        {/* ⚠️ The drawer handle comes FIRST on a phone, where the top left is where a
            thumb expects it. */}
        {onOpenNav && (
          <button
            onClick={onOpenNav}
            aria-label="Menu"
            style={{
              display: 'flex', alignItems: 'center', background: 'none', border: 'none',
              color: TEXT.body, cursor: 'pointer', padding: '2px', flexShrink: 0,
            }}
          ><MenuIcon /></button>
        )}

        <NavLink to="/" style={{ display: 'flex', alignItems: 'center', gap: '9px', textDecoration: 'none' }}>
          <img src="/avatars/league_logo.png" alt="" width={28} height={28} style={{ borderRadius: '50%' }} />
          {!isMobile && (
            <>
              <span style={{ ...font(800, 20, 1, '-0.02em'), color: TEXT.strong }}>Floosball</span>
              <span style={{
                ...font(700, 9, 1, '0.02em'),
                color: ACCENT.warning,
                background: 'rgba(245,158,11,0.28)',
                padding: '3px 5px',
              }}>BETA</span>
            </>
          )}
        </NavLink>

        {!isMobile && <span style={{ width: '1px', height: '22px', background: BORDER.hairline }} />}

        {!isMobile && seasonState.seasonNumber > 0 && (
          <>
            <span style={{ ...font(600, 13), color: TEXT.secondary }}>Season {seasonState.seasonNumber}</span>
            {/* The design specifies #64748b here. Raised to the codebase's readable-text
                floor — 13px at #64748b on #0f172a lands under 4.5:1, and the standings
                handoff (the latest of the three) sets #94a3b8 as the floor for any label. */}
            <span style={{ ...font(400, 13), color: TEXT.muted }}>{weekLabel}</span>
          </>
        )}

        {!isMobile && champion && (
          <NavLink
            to={`/team/${champion.id}`}
            style={{
              display: 'flex', alignItems: 'center', gap: '8px',
              paddingLeft: '10px', borderLeft: `1px solid ${BORDER.raised}`,
              textDecoration: 'none',
            }}
          >
            <img src={`/avatars/${champion.id}.png`} alt="" width={22} height={22} style={{ flexShrink: 0, borderRadius: '50%' }} />
            <span style={{ ...font(500, 15), color: ACCENT.warning, whiteSpace: 'nowrap' }}>
              {champion.city} {champion.name}
            </span>
            <TrophyIcon />
          </NavLink>
        )}

        {isMobile && seasonState.seasonNumber > 0 && (
          <span style={{ ...font(500, 11), color: TEXT.muted, whiteSpace: 'nowrap' }}>
            {`W${seasonState.currentWeek}`}
          </span>
        )}

        <span style={{ flex: 1 }} />

        <button
          onClick={() => setShowPalette(true)}
          aria-label="Search"
          style={{
            display: 'flex', alignItems: 'center', gap: '7px',
            background: 'none', border: 'none', cursor: 'pointer',
            padding: 0, fontFamily: FONT, color: TEXT.muted,
          }}
          onMouseEnter={e => { e.currentTarget.style.color = TEXT.body }}
          onMouseLeave={e => { e.currentTarget.style.color = TEXT.muted }}
        >
          {/* ⚠️ No keyboard shortcut, and so no shortcut hint (owner). Cmd+K is
              Chrome's own focus-the-address-bar binding, and preventDefault-ing it
              took that away from the reader inside this one tab. The button is the
              way in. */}
          <SearchIcon />
        </button>

        {user && <FantasyTicker userId={user.id} />}

        {user && (
          /* ⚠️ THE ENDOWMENT INDICATOR LIVES ON THIS COUNTER, and did not survive the
             redesign: it was built inline on the original Navbar, this header did not
             carry it across, and an active Endowment showed nothing anywhere. It reads
             from a shared hook rather than a second copy of the fetch, for the same
             reason the account menu is imported from Navbar instead of rebuilt. */
          <HoverTooltip
            text={endowment
              ? `Endowment active - +${endowment.boostPercent ?? 25}% on all Floobit income${endowment.weeksRemaining ? ` - ${endowment.weeksRemaining}w left` : ''}`
              : undefined}
            color={ACCENT.warning}
          >
            <button
              onClick={() => setShowShop(true)}
              style={{
                display: 'flex', alignItems: 'center', gap: '6px',
                background: 'none', cursor: 'pointer',
                ...font(700, 13), color: ACCENT.warning, ...TABULAR,
                fontFamily: FONT,
                // Off by default so the counter keeps its flat, borderless look; the
                // padding is always applied so the row does not shift when it lights up.
                border: `1px solid ${endowment ? `${ACCENT.warning}8c` : 'transparent'}`,
                padding: '3px 7px',
              }}
            >
              {/* ⚠️ THE MARK REPLACES BOTH THE COIN AND THE TRAILING "F". That unit label
                  existed because a plain filled circle said "this is money" without saying
                  WHICH money, which mattered once an FP ticker landed beside it. A currency
                  symbol does that job by itself — keeping the F as well would name the
                  currency twice in four characters.
                  ⚠️ So this chip is now [mark][number] while the FP chip stays
                  [glyph][number][unit], and that asymmetry is correct: FP has no symbol to
                  carry its own name, and Floobits now do. */}
              <FloobitSymbol size={14} />
              {(user.floobits ?? 0).toLocaleString()}
              {endowment && (
                <span style={{
                  ...font(800, 9, 1.4, '0.03em'),
                  color: BG.page, background: ACCENT.warning,
                  padding: '1px 4px', flexShrink: 0,
                }}>
                  +{endowment.boostPercent ?? 25}%
                </span>
              )}
            </button>
          </HoverTooltip>
        )}

        {user ? (
          <div style={{ position: 'relative' }}>
            <button
              className="plate"
              onClick={() => setShowUserMenu(o => !o)}
              style={{
                display: 'flex', alignItems: 'center', gap: '9px',
                background: BG.card, border: `1px solid ${BORDER.hairline}`,
                padding: '5px 10px 5px 6px', cursor: 'pointer', fontFamily: FONT,
              }}
            >
              {user.favoriteTeamId && (
                <img src={`/avatars/${user.favoriteTeamId}.png`} alt="" width={22} height={22} style={{ flexShrink: 0, borderRadius: '50%' }} />
              )}
              {!isMobile && (
                <span style={{ ...font(600, 12), color: TEXT.secondary, maxWidth: '160px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                  {user.username || 'User'}
                </span>
              )}
              {unreadCount > 0 && (
                <span style={{ width: '6px', height: '6px', borderRadius: '50%', background: ACCENT.info, flexShrink: 0 }} />
              )}
            </button>
            {showUserMenu && (
              <UserDropdown
                onClose={() => setShowUserMenu(false)}
                notifications={notifications}
                onMarkAllRead={markAllRead}
                onOpenTeamPicker={() => setShowTeamPicker(true)}
              />
            )}
          </div>
        ) : null}
      </header>

      <FavoriteTeamModal visible={showTeamPicker} onClose={() => setShowTeamPicker(false)} />
      {showShop && <ShopModal isOpen={showShop} onClose={() => setShowShop(false)} />}
      <CommandPalette open={showPalette} onClose={() => setShowPalette(false)} />
    </>
  )
}

/**
 * This week's fantasy points, beside the Floobits they will become.
 *
 * It is the WEEK, not the season, because the week is the number that moves — that is
 * what makes it a ticker rather than a statistic, and the season total is one hover away.
 * The two chips are deliberately the same shape as each other: a glyph and a number, no
 * pill, no background. They are the two currencies the reader is accumulating.
 *
 * ⚠️ Hidden once the regular season is over. Fantasy scoring stops at week 28, so past
 * that the number is frozen and reads as broken rather than final.
 */
const FantasyTicker: React.FC<{ userId?: number }> = ({ userId }) => {
  const { seasonState } = useFloosball()
  const { myEntry } = useFantasySnapshot(userId)

  if (!myEntry) return null
  if (seasonState.seasonComplete || seasonState.currentWeek > REGULAR_SEASON_WEEKS) return null

  const week = (myEntry.weekPlayerFP ?? 0) + (myEntry.weekCardBonus ?? 0)
  const season = myEntry.seasonTotal ?? 0

  return (
    <HoverTooltip
      text={`${season.toFixed(1)} FP this season${myEntry.rank ? `, ranked ${myEntry.rank}` : ''}`}
      color={ACCENT.live}
    >
      <NavLink
        to="/fantasy"
        style={{
          display: 'flex', alignItems: 'center', gap: '6px',
          textDecoration: 'none', ...font(700, 13), color: ACCENT.live, ...TABULAR,
        }}
      >
        <svg width="14" height="14" viewBox="0 0 20 20" fill="currentColor" style={{ flexShrink: 0 }}>
          <path d="M10 1l2.6 5.5 6 .8-4.4 4.2 1.1 6L10 14.6 4.7 17.5l1.1-6L1.4 7.3l6-.8L10 1z" />
        </svg>
        {week.toFixed(1)}
        <span style={{ ...font(600, 10, 1, '0.02em'), color: ACCENT.success }}>FP</span>
      </NavLink>
    </HoverTooltip>
  )
}

export default AppHeader
